import type { ReadonlyObservableSet } from "ecce-react";
import type { IconComponent } from "../../components/atoms/icons/icon-component";
import type { ValueOrI18nKeyProp } from "../../util/type";
import type { TranslationService } from "../translation/translation-service";
import type { JSX } from "react";
import { ObservableSet, bound, observable } from "ecce-react";


export type NotificationSeverity = "success" | "info" | "warning" | "error";

export type NotificationConfig = {
	severity: NotificationSeverity;
	icon?: IconComponent;
	title: string | JSX.Element;
	message: string | JSX.Element | null;
};

export type SendNotificationOptions = (
	& Omit<NotificationConfig, "title" | "message">
	& ValueOrI18nKeyProp<"title">
	& Partial<ValueOrI18nKeyProp<"message">>
);

export class Notification {
	static readonly #DURATION = 6 * 1000;

	readonly #notificationService: NotificationService;

	readonly id: number;
	readonly severity: NotificationSeverity;
	readonly title: string | JSX.Element;
	readonly message: string | JSX.Element | null;
	readonly icon: IconComponent | null;
	readonly #timestamp: number;

	#open = true;
	#dismissTimeout: ReturnType<typeof setTimeout>;

	constructor(notificationService: NotificationService, id: number, config: Readonly<NotificationConfig>) {
		this.#notificationService = notificationService;
		this.id = id;
		this.severity = config.severity;
		this.title = config.title;
		this.message = config.message;
		this.icon = config.icon ?? null;
		this.#timestamp = Date.now();

		this.#dismissTimeout = setTimeout(this.dismiss, Notification.#DURATION);
	}

	@observable() private set open(value: boolean) { this.#open = value; }
	get open(): boolean { return this.#open;}

	@bound()
	dismiss(): void {
		this.open = false;
		clearTimeout(this.#dismissTimeout);
		this.#notificationService["onDismissed"](this);
	}

	get age(): number {
		return Date.now() - this.#timestamp;
	}
}

export type NotificationServiceDependencies = {
	translation: TranslationService;
};
export class NotificationService {
	/**
	 * Milliseconds to wait after a notification is dismissed to actually delete
	 * it from the array of notifications, to allow the transition to complete.
	 */
	static readonly #CLEANUP_TIMEOUT = 5 * 1000;

	readonly #translation: TranslationService;

	#nextId = 0;
	readonly #notifications = new ObservableSet<Notification>();

	constructor(deps: NotificationServiceDependencies) {
		this.#translation = deps.translation;
	}

	send(config: Readonly<SendNotificationOptions>): void {
		for(const previousNotification of this.#notifications) {
			previousNotification.dismiss();
		}

		this.#notifications.add(new Notification(this, this.#nextId++, {
			...config,
			title: this.#translation.resolveValueOrI18nKey(config.title, config.titleKey, config.titleValues),
			message: this.#translation.resolveValueOrI18nKey(config.message, config.messageKey, config.messageValues, null),
		}));
	}

	/**
	 * Called by `Notification`.
	 */
	private onDismissed(notification: Notification) {
		setTimeout(() => {
			this.#notifications.delete(notification);
		}, NotificationService.#CLEANUP_TIMEOUT);
	}

	dismissAll(): void {
		for(const notification of this.notifications) {
			notification.dismiss();
		}
	}


	get notifications(): ReadonlyObservableSet<Notification> {
		return this.#notifications;
	}
}
